<template>
    <div class="corporate-sidebar tw:flex tw:flex-col tw:flex-1 tw:min-h-0">
        <div class="tw:flex-1 tw:min-h-0">
            <sidebar-menu
                :items="items"
                :include-home="false"
                :collapse-on-navigate="false"
                has-icon
                @update:expanded="$emit('update:expanded', $event)"
            >
                <!--
                    Expanded (tree) mode: PrimeVue Tree's row template uses
                    SidebarMenu's `#nodeIcon` slot to render the per-item
                    icon next to the label.
                -->
                <template #nodeIcon="{ node }">
                    <component
                        :is="node.data.iconComponent"
                        v-if="node.data?.iconComponent"
                        :size="18"
                        weight="regular"
                        class="tw:mr-2 tw:inline-block tw:align-middle"
                    />
                </template>

                <!--
                    Collapsed (summary) mode: without this slot,
                    AppSidebarSummaryItem would receive an undefined
                    `:icon` and fall back to SidebarMenu's `'link'` default,
                    causing every collapsed admin item to render as the
                    same chain-link Unicons glyph. Providing
                    `#summaryIcon` replaces the default `<e-icon>` glyph
                    with the same Phosphor SFC we render in expanded mode
                    (via `iconComponent`), so the two states stay visually
                    consistent. `iconComponent` lives on the original item
                    payload, surfaced here as `item.data.iconComponent`
                    (SidebarMenu's convertItemsToNodes spreads each input
                    item into `node.data`).
                -->
                <template #summaryIcon="{ item }">
                    <component
                        :is="item.data.iconComponent"
                        v-if="item.data?.iconComponent"
                        :size="18"
                        weight="regular"
                    />
                </template>
            </sidebar-menu>
        </div>

        <div class="corporate-sidebar-footer" :class="{ 'is-collapsed': isCollapsed }">
            <router-link
                :to="{ name: 'corporate-settings' }"
                class="corporate-sidebar-footer-link"
                :class="{ 'is-collapsed': isCollapsed }"
                :title="isCollapsed ? 'Manage Settings' : null"
                :aria-label="isCollapsed ? 'Manage Settings' : null"
            >
                <!--
                    Phosphor SFC, sized 18px to match #nodeIcon above so the
                    footer cog visually aligns with Manage Course / Manage Users
                    icons. `weight="regular"` matches the same default Jason
                    uses across this sidebar.
                -->
                <PhGear :size="18" weight="regular" />
                <!--
                    The footer link lives outside SidebarMenu, so it doesn't
                    get the summary/expanded treatment automatically. When the
                    sidebar collapses (`sidebarSummaryMode`), drop the label and
                    center the cog so this row matches the icon-only Manage
                    Course / Manage Users rows above instead of leaking text
                    into the narrow rail. The full label stays reachable via the
                    `title` tooltip / `aria-label`.
                -->
                <span v-if="!isCollapsed">Manage Settings</span>
            </router-link>
        </div>
    </div>
</template>

<script>
import { PhBookOpenText, PhCertificate, PhGear, PhUser } from '@phosphor-icons/vue';
import SidebarMenu from '@/components/sidebar/SidebarMenu.vue';
import { useLayout } from '@/layouts/composables/layout.js';

export default {
    name: 'CorporateAdminSidebar',

    components: { PhGear, SidebarMenu },

    emits: ['update:expanded'],

    setup() {
        // Same collapse signal SidebarMenu reads, so the footer link stays in
        // lockstep with the menu items' summary/expanded state.
        const { layoutState } = useLayout();

        return { layoutState };
    },

    computed: {
        isCollapsed() {
            return this.layoutState.sidebarSummaryMode;
        },

        items() {
            // Icons are Phosphor components rendered via SidebarMenu's
            // `#nodeIcon` slot. `iconComponent` is intentionally used
            // instead of `icon` so the PrimeVue Tree does not also try to
            // render a `pi pi-...` glyph next to the Phosphor SVG.
            return [
                {
                    type: 'item',
                    order: 1,
                    title: 'Manage Courses',
                    iconComponent: PhBookOpenText,
                    route: { name: 'courses' },
                },
                {
                    type: 'item',
                    order: 2,
                    title: 'Manage Users',
                    iconComponent: PhUser,
                    route: { name: 'corporate-manage-users' },
                },
                {
                    type: 'item',
                    order: 3,
                    title: 'Certificates',
                    iconComponent: PhCertificate,
                    route: { name: 'corporate-admin-certificates' },
                },
            ];
        },
    },
};
</script>

<style scoped lang="scss">
// Height/pinning of this sidebar is handled by flexbox, not a fixed height:
// the template's `tw:flex-1 tw:min-h-0` lets it grow to fill whatever space the
// parent `.layout-menu-container` gives it. In the mobile drawer that container
// also holds AppSidebar's close (X) button, so growing (rather than a rigid
// `h-full`) means the sidebar takes the remaining height BELOW the button and
// the footer — the only entry point to Manage Settings — stays on screen. On
// desktop there's no close button, so it fills the rail and the cog pins to the
// bottom. The parent is made a flex column in `_sidebar_vertical.scss` (corporate
// scope). This replaces the earlier viewport media query, which broke below the
// 1200px drawer breakpoint by bunching the footer up under the menu.

.corporate-sidebar-footer {
    border-top: 1px solid var(--surface-border, rgba(0, 0, 0, 0.08));
    // No horizontal padding here — the link itself owns the indent so its
    // hover background can span the full footer width, matching how a
    // PrimeVue tree row's hover surface spans the full tree width.
    padding-block: 0.5rem;
}

.corporate-sidebar-footer-link {
    display: flex;
    align-items: center;
    // Collapse-jitter fix (868kar1y2). Pin the row to the same 40px height the
    // nav rows use, in BOTH states. Previously the footer height changed with
    // its content — ~40px expanded (label present, and the label even wrapped to
    // multiple lines) vs ~26px collapsed (icon only). Because the footer is
    // bottom-anchored inside the fixed-height sidebar, that height delta shoved
    // the version indicator + gear ~45px on every collapse/expand. A constant
    // height keeps the whole bottom stack still.
    min-height: 40px;
    // Never let "Manage Settings" wrap — wrapping was inflating the expanded
    // footer height and amplifying the jump.
    white-space: nowrap;
    // Matches the menu rows' icon→label spacing: SidebarMenu's `.tree-node-link`
    // has `gap: 0.5rem` and its icon adds `tw:mr-2` (0.5rem) on top, so the cog
    // needs a full 1rem to line up the label start with Manage Course / Users.
    gap: 1rem;
    text-decoration: none;
    color: inherit;
    width: 100%;
    // Left indent lines the cog up with the Manage Course / Manage Users icons.
    // The footer sits OUTSIDE SidebarMenu, so it has to reproduce the full inset
    // those rows get from the aside's `.is-expanded` padding PLUS the PrimeVue
    // Tree's internal row structure (root padding + the hidden leaf toggle-button
    // slot). Measured against the live DOM the menu icons land 2.7rem (≈43px)
    // from the sidebar's left edge — token math alone under-counts this — so we
    // match that exactly here. 0.25rem vertical matches `.p-tree-node-content`'s
    // `padding-block`. Collapsed mode resets the inline padding below.
    padding: 0.25rem 0.95rem 0.25rem 2.7rem;
    // Match the menu rows' label size (SidebarMenu's `.tree-node-link` is 1rem).
    // The footer sits OUTSIDE SidebarMenu, so it doesn't inherit that rule and
    // would otherwise render at the 18px body size ($size-7 = 1.125rem).
    font-size: 1rem;
    // Menu rows render at the body's normal weight (the `.menu` aside's Bulma
    // semibold class doesn't actually reach the PrimeVue Tree text), so the
    // footer must stay normal too — 600 here is what made "Manage Settings"
    // read as bold next to them.
    font-weight: normal;

    &:hover {
        background-color: var(--surface-hover);
        text-decoration: none;
    }

    &.router-link-active {
        font-weight: 700;
    }

    // Collapsed (summary) rail: no label, so center the cog in the narrow
    // rail to line up with the icon-only Manage Course / Manage Users rows
    // above. Drop the gap (no sibling text to space from) and the horizontal
    // indent that only made sense when a left-aligned label followed.
    &.is-collapsed {
        justify-content: center;
        gap: 0;
        padding-inline: 0;
    }
}
</style>
